import { useState, useRef, useCallback, useEffect } from "react";
import type { UIMessage } from "ai";
import type { TTSWorkerResponse, VoiceState } from "./types";
import { AudioPlayer } from "./audio-player";
import { AudioCache } from "./audio-cache";
import { DEFAULT_VOICE_ID } from "./voices";
import { stripMarkdown } from "../../shared/strip-markdown.js";
import { audioDebug } from "./debug-overlay";

// Cap TTS text to prevent memory exhaustion from very long messages (~25 min of speech)
const MAX_TTS_CHARS = 50_000;

function extractTextFromMessage(message: UIMessage): string {
  const textParts = message.parts
    .filter((p): p is Extract<typeof p, { type: "text" }> => p.type === "text")
    .map((p) => p.text);
  return stripMarkdown(textParts.join("")).slice(0, MAX_TTS_CHARS);
}

interface UseVoiceOptions {
  voiceEnabled: boolean;
  voiceId: string;
  autoSpeak: boolean;
  playbackSpeed?: number;
  // Server TTS
  ttsMode?: "browser" | "server";
  sendTTSRequest?: (messageId: string, text: string, voiceId: string, requestId: string) => void;
  sendTTSStop?: () => void;
}

interface UseVoiceReturn {
  voiceState: VoiceState;
  speakingMessageId: string | null;
  initVoice: () => void;
  speakMessage: (message: UIMessage) => void;
  /** General id/text primitive `speakMessage` wraps — read-aloud slice 1
   *  (the Writing Room's chapter reader) drives this directly with a
   *  content-addressed chapter id instead of a UIMessage. */
  speakText: (id: string, text: string) => void;
  stopSpeaking: () => void;
  seekTo: (messageId: string, fraction: number) => void;
  getPlaybackProgress: () => number;
  getAudioLevel: () => number;
  audioCache: AudioCache;
  restartVoice: () => void;
  playbackSpeed: number;
  /** Reeve HIGH, PR #68 2nd round — the id a server-TTS generation most
   *  recently failed for (not a generic flag, deliberately: a single
   *  shared `voiceState: 'error'` would mark EVERY TTS consumer as broken
   *  for a failure that only ever concerned one specific request). Set by
   *  onTTSError below, ownership-checked the same way onTTSEnd already is
   *  so a stale/superseded notice for a request this instance no longer
   *  owns can never set it. Cleared at the top of every speakText call —
   *  starting anything new (including a retry of the SAME id) wipes the
   *  previous failure marker first. */
  erroredMessageId: string | null;
  /** Rainer/Thorn (2026-10-10) — the id of the most recent generation whose
   *  TERMINAL signal (server tts_end/generation-done, or a cache replay that
   *  was already complete when it started) has actually arrived — distinct
   *  from `speakingMessageId` going null, which can ALSO happen mid-
   *  generation on a player underrun (PR #70's short first piece drains
   *  before the next piece has finished synthesizing) or a manual seek into
   *  a still-incomplete cache entry. A consumer that needs to know "is this
   *  id's audio FULLY generated" (the Writing Room's chapter queue decides
   *  whether to advance off this, not off speakingMessageId alone) must
   *  check this, not just a null transition. Cleared at the top of every
   *  speakText call for a FRESH (non-cache) dispatch — same discipline as
   *  erroredMessageId — and set immediately for a cache-hit replay, since a
   *  complete cache entry's generation is trivially already done. */
  ttsEndedId: string | null;
  // Callbacks for useChat to wire binary frames into — only used in server TTS mode
  onServerAudioChunk: (data: ArrayBuffer) => void;
  onTTSStart: (messageId: string, sampleRate: number, voiceId: string, sourceId?: string) => void;
  onTTSEnd: (messageId: string, sourceId?: string) => void;
  onTTSError: (messageId: string, sourceId?: string) => void;
}

export function useVoice(
  messages: UIMessage[],
  status: string,
  options: UseVoiceOptions,
): UseVoiceReturn {
  const [voiceState, setVoiceState] = useState<VoiceState>("unloaded");
  const downloadProgressRef = useRef(0);
  const deviceTypeRef = useRef<"webgpu" | "wasm" | null>(null);
  const [speakingMessageId, setSpeakingMessageId] = useState<string | null>(null);
  // See UseVoiceReturn's own doc comment on erroredMessageId above.
  const [erroredMessageId, setErroredMessageId] = useState<string | null>(null);
  // See UseVoiceReturn's own doc comment on ttsEndedId above.
  const [ttsEndedId, setTtsEndedId] = useState<string | null>(null);
  // Ref mirrors, updated synchronously alongside the state setters above
  // via markTtsEnded/markErrored (never set directly elsewhere) — Thorn
  // (2026-10-10 review, round 2): onTTSStart needs an always-current,
  // synchronous read of "has the request lane's own outstanding dispatch
  // already reached ITS terminal" to tell a genuinely NEW cross-lane
  // confirmation apart from a stale straggler for an id we're still
  // exclusively waiting on; a state value read inside a useCallback risks
  // a stale closure across a rapid sequence of calls within the same
  // `act()` (or, in production, the same synchronous WS-message handler).
  const ttsEndedIdRef = useRef<string | null>(null);
  const erroredMessageIdRef = useRef<string | null>(null);
  const markTtsEnded = useCallback((id: string | null) => {
    ttsEndedIdRef.current = id;
    setTtsEndedId(id);
  }, []);
  const markErrored = useCallback((id: string | null) => {
    erroredMessageIdRef.current = id;
    setErroredMessageId(id);
  }, []);

  const workerRef = useRef<Worker | null>(null);
  const playerRef = useRef<AudioPlayer>(new AudioPlayer());
  const cacheRef = useRef<AudioCache>(new AudioCache());
  const voiceIdRef = useRef(options.voiceId || DEFAULT_VOICE_ID);
  const drainIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Generalized (read-aloud slice 1) from `UIMessage | null` — the queue-
  // while-loading slot only ever needed an id + already-extracted text, and
  // a chapter reader has neither a UIMessage nor extractTextFromMessage's
  // markdown-part-joining shape to give it. speakMessage below still passes
  // a UIMessage's own id/text through this same shape.
  const pendingMessageRef = useRef<{ id: string; text: string } | null>(null);
  // Ref mirror of speakingMessageId — used for toggle check in speakMessage to avoid
  // stale closure reads when state hasn't propagated between rapid click events.
  const speakingMessageIdRef = useRef<string | null>(null);
  // Browser/cached manual replay guard. Server-stream routing is bound separately
  // by message + generation so a stale replay cannot suppress current audio.
  const isManualReplayRef = useRef(false);
  const serverTtsSourceRef = useRef<{
    generation: number;
    kind: "none" | "cache" | "request" | "auto";
    requestedMessageId: string | null;
    sourceId: string | null;
    activeMessageId: string | null;
  }>({ generation: 0, kind: "none", requestedMessageId: null, sourceId: null, activeMessageId: null });
  // Rainer/Thorn (2026-10-10) — persistent snapshots of the most recently
  // DISPATCHED request/stream's ownership tokens, independent from
  // serverTtsSourceRef above. That ref gets reset to kind:"none" the moment
  // playback drains (startDrainPolling's own completion branch) — correctly
  // so, since a stale "request"/"auto" kind lingering there would otherwise
  // make a LATER, unrelated auto-stream's onTTSStart silently bail (see its
  // own `source.kind === "request"` branch). But that same reset severs the
  // ownership info onTTSEnd/onTTSError need to recognize a LATE-arriving
  // terminal signal for a generation that drained EARLY (an underrun, or a
  // seek into a still-incomplete cache entry) without ever actually being
  // superseded server-side — these refs survive that reset.
  //
  // TWO separate refs, not one shared slot (Reeve, 2026-10-10 review,
  // cross-lane PR #72 round 2): a manual one-shot (the Writing Room's
  // chapter reader) and an auto-stream (a chat message's own playback) can
  // each have their OWN outstanding dispatch at the same time — the server
  // decides to cross-cancel one lane for the other (TTSEngine.
  // requestSuccessor), but THIS client-side bookkeeping must not conflate
  // them. A shared single slot meant the auto lane's own confirmation
  // (onTTSStart) unconditionally overwrote the manual lane's dispatch
  // record the instant it arrived — so when the manual request's OWN
  // later terminal signal arrived (the gateway's handleManualTTSRequest
  // onError still fires tts_end + tts_notice for a cross-mode-superseded
  // one-shot; `ownsRequest()` there is keyed on manualTtsRequestByWs,
  // which an auto-stream dispatch never touches), onTTSEnd/onTTSError's
  // ownership check against the clobbered ref silently failed, and the
  // Writing Room's chunk queue was left stalled forever with no error.
  // Each lane's OWN dispatch naturally still overwrites its OWN slot (a
  // new manual request supersedes the OLD manual one; a new auto-stream
  // supersedes the old auto one) — that same-lane supersede is correct,
  // existing behavior, untouched by this split.
  const lastRequestDispatchRef = useRef<{ requestedMessageId: string; sourceId: string | null } | null>(null);
  const lastAutoDispatchRef = useRef<{ requestedMessageId: string; sourceId: string | null } | null>(null);
  /** Shared ownership check for onTTSEnd/onTTSError — matches against
   *  EITHER lane's own dispatch record; which lane matched doesn't matter
   *  to either caller (both just need to know "is this OUR terminal
   *  signal," then record ttsEndedId/erroredMessageId — a consumer like
   *  the Writing Room's chunk queue only ever compares that against ITS
   *  OWN chunk ids, which structurally can never collide with a chat
   *  message's auto-stream id). */
  const ownsDispatchedTerminal = useCallback((messageId: string, sourceId: string | null) => {
    const matches = (lane: { requestedMessageId: string; sourceId: string | null } | null) =>
      lane !== null && lane.requestedMessageId === messageId && lane.sourceId === sourceId;
    return matches(lastRequestDispatchRef.current) || matches(lastAutoDispatchRef.current);
  }, []);
  // Rainer/Thorn (2026-10-10) — mirrors serverTtsSourceRef.activeMessageId
  // (set only once onTTSStart actually CONFIRMS a request/stream is bound —
  // never at dispatch time, so a stray frame from an abandoned generation
  // can't get misattributed to a brand new, not-yet-confirmed dispatch; see
  // onServerAudioChunk below) but, like lastRequestDispatchRef/
  // lastAutoDispatchRef above, survives the drain-complete reset. Cleared
  // on every new replaceServerTtsSource call
  // EXCEPT the drain-complete one (preserveConfirmedActive=true there) —
  // see that call site's own comment.
  const lastConfirmedActiveRef = useRef<{ messageId: string; sourceId: string | null } | null>(null);
  const replaceServerTtsSource = useCallback((
    kind: "none" | "cache" | "request" | "auto",
    requestedMessageId: string | null = null,
    sourceId: string | null = null,
    preserveConfirmedActive = false,
  ) => {
    const generation = serverTtsSourceRef.current.generation + 1;
    serverTtsSourceRef.current = { generation, kind, requestedMessageId, sourceId, activeMessageId: null };
    if (!preserveConfirmedActive) lastConfirmedActiveRef.current = null;
    return generation;
  }, []);
  // Refs for auto-speak missed message on model ready (Bug 5)
  const autoSpeakRef = useRef(options.autoSpeak);
  const voiceEnabledRef = useRef(options.voiceEnabled);
  const messagesRef = useRef(messages);

  useEffect(() => { autoSpeakRef.current = options.autoSpeak; }, [options.autoSpeak]);
  useEffect(() => { voiceEnabledRef.current = options.voiceEnabled; }, [options.voiceEnabled]);
  useEffect(() => { messagesRef.current = messages; }, [messages]);

  // Server TTS option refs — mirror props so callbacks stay stable
  const ttsModeRef = useRef(options.ttsMode ?? "browser");
  const sendTTSRequestRef = useRef(options.sendTTSRequest);
  const sendTTSStopRef = useRef(options.sendTTSStop);
  const playbackSpeedRef = useRef(options.playbackSpeed ?? 1.0);
  useEffect(() => { ttsModeRef.current = options.ttsMode ?? "browser"; }, [options.ttsMode]);
  useEffect(() => { sendTTSRequestRef.current = options.sendTTSRequest; }, [options.sendTTSRequest]);
  useEffect(() => { sendTTSStopRef.current = options.sendTTSStop; }, [options.sendTTSStop]);
  useEffect(() => { playbackSpeedRef.current = options.playbackSpeed ?? 1.0; }, [options.playbackSpeed]);

  // Always update both state and ref together so toggle checks never read stale values
  const updateSpeakingId = useCallback((id: string | null) => {
    speakingMessageIdRef.current = id;
    setSpeakingMessageId(id);
  }, []);

  // Keep voiceId ref in sync — and force-stop any active playback/streaming
  // so the next generation uses the new voice.
  useEffect(() => {
    const prev = voiceIdRef.current;
    voiceIdRef.current = options.voiceId || DEFAULT_VOICE_ID;

    if (prev !== voiceIdRef.current) {
      // Voice changed — kill current playback so next gen uses the new voice
      isManualReplayRef.current = false;
      replaceServerTtsSource("none");
      playerRef.current.stop();
      workerRef.current?.postMessage({ type: "stop" });
      updateSpeakingId(null);
    }
  }, [options.voiceId, updateSpeakingId, replaceServerTtsSource]);

  // Both TTS modes now generate audio natively at the requested speed
  // (browser: Kokoro speed param; server: gateway speed param), so playbackRate
  // must stay at 1.0 in all cases — setting it above 1.0 would double-apply
  // the speed and shift pitch upward (Mickey Mouse effect).
  useEffect(() => {
    playerRef.current.playbackRate = 1.0;
  }, [options.playbackSpeed, options.ttsMode]);

  // Drain polling extracted into a reusable helper — used by both generation-done and cache replay
  const startDrainPolling = useCallback((forId: string, serverGeneration?: number) => {
    if (drainIntervalRef.current) clearInterval(drainIntervalRef.current);
    drainIntervalRef.current = setInterval(() => {
      if (!playerRef.current.isPlaying) {
        const sourceStillOwnsDrain = serverGeneration === undefined
          || serverTtsSourceRef.current.generation === serverGeneration;
        // Both message and server generation must still match. This protects a
        // rapid replay of the same message from an older drain poll.
        if (speakingMessageIdRef.current === forId && sourceStillOwnsDrain) {
          isManualReplayRef.current = false;
          // preserveConfirmedActive=true (Rainer/Thorn, 2026-10-10) — this
          // reset can fire on a PREMATURE drain (underrun/seek), well
          // before the server's own tts_end for a still-live, non-
          // superseded generation arrives. onServerAudioChunk's self-heal
          // needs lastConfirmedActiveRef to survive this exact reset so
          // more audio for that SAME generation can resume playback rather
          // than being silently dropped. Harmless on a genuine finish too
          // — nothing else arrives for an actually-done id regardless.
          replaceServerTtsSource("none", null, null, true);
          setVoiceState("ready");
          updateSpeakingId(null);
        }
        if (drainIntervalRef.current) {
          clearInterval(drainIntervalRef.current);
          drainIntervalRef.current = null;
        }
      }
    }, 200);
  }, [updateSpeakingId, replaceServerTtsSource]);

  const getOrCreateWorker = useCallback(() => {
    if (workerRef.current) return workerRef.current;

    let worker: Worker;
    try {
      worker = new Worker(
        new URL("./tts.worker.ts", import.meta.url),
        { type: "module" },
      );
    } catch (err) {
      console.error("[voice] Failed to create worker:", err);
      setVoiceState("error");
      return null;
    }

    worker.onerror = (err) => {
      console.error("[voice] Worker failed to load:", err);
      setVoiceState("error");
    };

    worker.onmessage = (e: MessageEvent<TTSWorkerResponse>) => {
      const msg = e.data;

      switch (msg.type) {
        case "init-progress":
          downloadProgressRef.current = msg.progress;
          break;

        case "ready":
          setVoiceState("ready");
          deviceTypeRef.current = msg.deviceType;
          downloadProgressRef.current = 100;
          // Speak queued text if the user clicked speak/listen while the model was loading
          if (pendingMessageRef.current) {
            const pending = pendingMessageRef.current;
            pendingMessageRef.current = null;
            if (pending.text) {
              playerRef.current.prime();
              updateSpeakingId(pending.id);
              workerRef.current?.postMessage({ type: "generate", text: pending.text, voiceId: voiceIdRef.current, speed: playbackSpeedRef.current });
            }
          } else if (autoSpeakRef.current && voiceEnabledRef.current) {
            // Auto-speak missed message — model finished loading after stream already completed
            const msgs = messagesRef.current;
            const lastMsg = msgs[msgs.length - 1];
            if (lastMsg && lastMsg.role === "assistant") {
              const text = extractTextFromMessage(lastMsg);
              if (text) {
                playerRef.current.prime();
                updateSpeakingId(lastMsg.id);
                workerRef.current?.postMessage({ type: "generate", text, voiceId: voiceIdRef.current, speed: playbackSpeedRef.current });
              }
            }
          }
          break;

        case "audio-chunk": {
          // Skip streaming chunks that arrive after manual replay started
          if (isManualReplayRef.current) break;
          // Cache the chunk before enqueueing for playback
          const cacheKey = speakingMessageIdRef.current
            ? AudioCache.makeKey(speakingMessageIdRef.current, voiceIdRef.current)
            : null;
          if (cacheKey) {
            cacheRef.current.append(cacheKey, msg.samples, msg.sampleRate);
          }
          playerRef.current.enqueue(msg.samples, msg.sampleRate);
          setVoiceState("speaking");
          break;
        }

        case "generation-done": {
          // Mark cache entry complete so replays know all chunks are in
          const doneCacheKey = speakingMessageIdRef.current
            ? AudioCache.makeKey(speakingMessageIdRef.current, voiceIdRef.current)
            : null;
          if (doneCacheKey) {
            cacheRef.current.markComplete(doneCacheKey);
          }
          // The browser engine's one-shot generate() call has now produced
          // ALL audio for this id — record the terminal signal regardless
          // of whether playback has drained yet (see ttsEndedId's own doc
          // comment on useVoice's return value).
          if (speakingMessageIdRef.current) {
            markTtsEnded(speakingMessageIdRef.current);
          }
          // Poll until audio drains — onended fires per-source, not globally
          startDrainPolling(speakingMessageIdRef.current ?? "");
          break;
        }

        case "busy":
          // Worker is mid-stream; manual replay request was dropped — reset UI cleanly
          isManualReplayRef.current = false;
          updateSpeakingId(null);
          setVoiceState(prev => prev === "speaking" ? "ready" : prev);
          break;

        case "error":
          console.error("[voice] Worker error:", msg.message);
          setVoiceState("error");
          updateSpeakingId(null);
          break;
      }
    };

    workerRef.current = worker;
    return worker;
  }, [startDrainPolling, updateSpeakingId]);

  const initVoice = useCallback(() => {
    if (voiceState === "loading" || voiceState === "ready") {
      return;
    }
    setVoiceState("loading");
    downloadProgressRef.current = 0;
    // Prime AudioContext during user gesture — avoids autoplay policy issues later
    playerRef.current.prime();
    const worker = getOrCreateWorker();
    if (!worker) return;
    worker.postMessage({ type: "init", voiceId: voiceIdRef.current });
  }, [voiceState, getOrCreateWorker]);

  const stopSpeaking = useCallback(() => {
    isManualReplayRef.current = false;
    replaceServerTtsSource("none");
    if (drainIntervalRef.current) {
      clearInterval(drainIntervalRef.current);
      drainIntervalRef.current = null;
    }
    playerRef.current.stop();
    if (ttsModeRef.current === "server") {
      sendTTSStopRef.current?.();
    } else {
      const worker = workerRef.current;
      if (worker) worker.postMessage({ type: "stop" });
    }
    updateSpeakingId(null);
    setVoiceState(prev => prev === "speaking" ? "ready" : prev);
  }, [updateSpeakingId, replaceServerTtsSource]);

  // General id/text primitive (read-aloud slice 1). `speakMessage` below is a
  // thin wrapper over this — every downstream mechanism (cache key, seekTo,
  // Waveform, drain polling, speakingMessageId) already keys by id, not by
  // UIMessage shape, so a caller with a non-message id/text pair (the Writing
  // Room's chapter reader) can drive the exact same pipe end to end.
  const speakText = useCallback(
    (id: string, text: string) => {
      // Any new call (including a retry of the SAME id) wipes a previous
      // failure marker first — see erroredMessageId's own doc comment.
      markErrored(null);
      // Same discipline for the generation-terminal marker — a retry of the
      // SAME id starts a fresh generation that has NOT ended yet (cleared
      // here; re-set below immediately for a cache hit, or later by the
      // real terminal signal for a fresh dispatch).
      markTtsEnded(null);

      // Toggle off if already speaking this id — read ref to avoid stale closure
      if (speakingMessageIdRef.current === id) {
        stopSpeaking();
        return;
      }

      if (!text) {
        return;
      }

      // Stop any active auto-speak stream before starting manual playback so
      // in-flight chunks don't collide with the replay audio in AudioPlayer.
      if (isStreamingRef.current) {
        isStreamingRef.current = false;
        streamVoiceIdRef.current = null;
        if (ttsModeRef.current === "server") {
          sendTTSStopRef.current?.();
        } else {
          workerRef.current?.postMessage({ type: "stop" });
        }
      }

      // ─── Server TTS path ───────────────────────────────────────────────────
      if (ttsModeRef.current === "server") {
        // Check cache first — server audio was cached the same as browser audio
        const cacheKey = AudioCache.makeKey(id, voiceIdRef.current);
        if (cacheRef.current.has(cacheKey)) {
          const cached = cacheRef.current.getMerged(cacheKey);
          if (cached) {
            isManualReplayRef.current = true;
            const serverGeneration = replaceServerTtsSource("cache", id);
            if (drainIntervalRef.current) {
              clearInterval(drainIntervalRef.current);
              drainIntervalRef.current = null;
            }
            playerRef.current.stop();
            playerRef.current.prime();
            updateSpeakingId(id);
            // cacheRef.current.has() above already required `complete` —
            // this id's generation is trivially already done.
            markTtsEnded(id);
            playerRef.current.playFromBuffer(cached.samples, cached.sampleRate);
            setVoiceState("speaking");
            startDrainPolling(id, serverGeneration);
            return;
          }
        }

        // No cache — replace any cached/manual drain with an explicit server
        // request generation. `tts_start(messageId, sourceId)` is the authority
        // that binds subsequent binary frames to this accepted request.
        isManualReplayRef.current = false;
        const requestId = `tts_${crypto.randomUUID()}`;
        replaceServerTtsSource("request", id, requestId);
        lastRequestDispatchRef.current = { requestedMessageId: id, sourceId: requestId };
        if (drainIntervalRef.current) {
          clearInterval(drainIntervalRef.current);
          drainIntervalRef.current = null;
        }
        playerRef.current.stop();
        playerRef.current.prime();
        updateSpeakingId(id);
        sendTTSRequestRef.current?.(id, text, voiceIdRef.current, requestId);
        return;
      }

      // ─── Browser TTS path (unchanged) ─────────────────────────────────────

      // Queue if model still loading — will auto-speak when ready
      if (voiceState === "loading") {
        pendingMessageRef.current = { id, text };
        updateSpeakingId(id);
        return;
      }

      // Not ready and not loading — trigger init, queue, and bail
      if (voiceState !== "ready" && voiceState !== "speaking") {
        pendingMessageRef.current = { id, text };
        updateSpeakingId(id);
        initVoice();
        return;
      }

      // Check cache for instant replay — skip TTS generation entirely
      const cacheKey = AudioCache.makeKey(id, voiceIdRef.current);
      if (cacheRef.current.has(cacheKey)) {
        const cached = cacheRef.current.getMerged(cacheKey);
        if (cached) {
          isManualReplayRef.current = true;
          playerRef.current.stop();
          playerRef.current.prime();
          updateSpeakingId(id);
          // cacheRef.current.has() above already required `complete`.
          markTtsEnded(id);
          playerRef.current.playFromBuffer(cached.samples, cached.sampleRate);
          setVoiceState("speaking");
          startDrainPolling(id);
          return;
        }
      }

      // Stop whatever's currently playing, prime AudioContext during user gesture
      isManualReplayRef.current = true;
      playerRef.current.stop();
      playerRef.current.prime();
      updateSpeakingId(id);

      const worker = getOrCreateWorker();
      if (!worker) return;
      worker.postMessage({ type: "generate", text, voiceId: voiceIdRef.current, speed: playbackSpeedRef.current });
    },
    [voiceState, initVoice, stopSpeaking, getOrCreateWorker, updateSpeakingId, startDrainPolling, replaceServerTtsSource],
  );

  const speakMessage = useCallback(
    (message: UIMessage) => {
      speakText(message.id, extractTextFromMessage(message));
    },
    [speakText],
  );

  // Seek to a fraction (0–1) within a cached message's audio
  const seekTo = useCallback((messageId: string, fraction: number) => {
    const cacheKey = AudioCache.makeKey(messageId, voiceIdRef.current);
    const cached = cacheRef.current.getMerged(cacheKey);
    if (!cached) return;

    const offsetSamples = Math.floor(fraction * cached.samples.length);
    playerRef.current.stop();
    playerRef.current.prime();
    updateSpeakingId(messageId);
    isManualReplayRef.current = true;
    playerRef.current.playFromBuffer(cached.samples, cached.sampleRate, offsetSamples);
    setVoiceState("speaking");
    startDrainPolling(messageId);
  }, [updateSpeakingId, startDrainPolling]);

  const getPlaybackProgress = useCallback(() => {
    return playerRef.current.getPlaybackProgress();
  }, []);

  // Hard reset + re-init — useful when voice gets stuck in a bad state
  const restartVoice = useCallback(() => {
    // Tear down
    isManualReplayRef.current = false;
    if (drainIntervalRef.current) {
      clearInterval(drainIntervalRef.current);
      drainIntervalRef.current = null;
    }
    playerRef.current.stop();
    if (workerRef.current) {
      workerRef.current.terminate();
      workerRef.current = null;
    }
    updateSpeakingId(null);
    pendingMessageRef.current = null;
    // Re-init immediately — set loading (not unloaded) to skip the guard in initVoice
    setVoiceState("loading");
    downloadProgressRef.current = 0;
    deviceTypeRef.current = null;
    playerRef.current.prime();
    const worker = getOrCreateWorker();
    if (worker) {
      worker.postMessage({ type: "init", voiceId: voiceIdRef.current });
    }
  }, [updateSpeakingId, getOrCreateWorker]);

  // --- Streaming TTS (auto-speak during LLM response) ---
  const streamedLengthRef = useRef(0);
  const isStreamingRef = useRef(false);
  const streamVoiceIdRef = useRef<string | null>(null);

  useEffect(() => {
    // Server TTS mode: gateway handles streaming audio; don't duplicate client-side
    if (ttsModeRef.current === "server") return;
    // Manual replay in progress — don't let auto-speak interrupt it
    if (isManualReplayRef.current) return;

    if (!options.voiceEnabled || !options.autoSpeak) return;
    if (voiceState !== "ready" && voiceState !== "speaking") return;

    const lastMessage = messages[messages.length - 1];
    if (!lastMessage || lastMessage.role !== "assistant") return;

    const currentText = extractTextFromMessage(lastMessage);

    if (status === "streaming") {
      const currentVoice = voiceIdRef.current;

      // Restart stream if voice changed mid-stream
      if (isStreamingRef.current && streamVoiceIdRef.current !== currentVoice) {
        const worker = workerRef.current;
        if (worker) {
          worker.postMessage({ type: "stop" });
          playerRef.current.stop();
        }
        isStreamingRef.current = false;
      }

      if (!isStreamingRef.current) {
        // Stream just started (or restarted after voice change)
        isStreamingRef.current = true;
        streamVoiceIdRef.current = currentVoice;
        streamedLengthRef.current = 0;
        playerRef.current.stop();
        playerRef.current.prime();
        updateSpeakingId(lastMessage.id);
        const worker = getOrCreateWorker();
        if (!worker) return;
        worker.postMessage({ type: "stream-start", voiceId: currentVoice, speed: playbackSpeedRef.current });
      }

      // Push delta (capped to prevent memory exhaustion)
      if (streamedLengthRef.current >= MAX_TTS_CHARS) return;
      const delta = currentText.slice(streamedLengthRef.current, MAX_TTS_CHARS);
      if (delta.length > 0) {
        streamedLengthRef.current = Math.min(currentText.length, MAX_TTS_CHARS);
        const worker = getOrCreateWorker();
        if (!worker) return;
        worker.postMessage({ type: "stream-push", text: delta });
      }
    } else if (isStreamingRef.current && (status === "idle" || status === "ready")) {
      // Stream ended — close the splitter
      isStreamingRef.current = false;
      streamVoiceIdRef.current = null;
      const worker = workerRef.current;
      if (worker) worker.postMessage({ type: "stream-end" });
    }
  }, [messages, status, options.voiceEnabled, options.autoSpeak, voiceState, getOrCreateWorker, updateSpeakingId]);

  // Reset streaming state and cache on session switch (messages array cleared)
  useEffect(() => {
    if (messages.length === 0) {
      isStreamingRef.current = false;
      streamedLengthRef.current = 0;
      streamVoiceIdRef.current = null;
      cacheRef.current.clear();
    }
  }, [messages.length]);

  // Tab visibility: intentionally NO pause/resume.
  // Suspending AudioContext on tab switch kills background playback and
  // desynchronizes scheduled sources (causing replay cutoff). Browsers
  // handle resource management for background tabs on their own.

  // --- Web Speech API fallback on worker init error ---
  const speakFallback = useCallback((text: string) => {
    if (!("speechSynthesis" in window)) return;
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.rate = 1.0;
    utterance.onend = () => {
      setVoiceState("error"); // stay in error — fallback is degraded
      updateSpeakingId(null);
    };
    window.speechSynthesis.speak(utterance);
  }, [updateSpeakingId]);

  // --- Server TTS callbacks ---
  // These are wired into useChat's refs so binary audio frames route here.

  // Stores the sample rate received in tts_start so onServerAudioChunk uses the real value
  const serverSampleRateRef = useRef(24000);

  const onServerAudioChunk = useCallback((data: ArrayBuffer) => {
    if (ttsModeRef.current !== "server") return;
    // lastConfirmedActiveRef (not serverTtsSourceRef.activeMessageId) —
    // survives the drain-complete reset (see its own doc comment) while
    // still only ever naming an id ONCE its OWN tts_start has confirmed
    // it, never at dispatch time; a stray frame from an abandoned
    // generation can therefore never get misattributed to a brand new,
    // not-yet-confirmed dispatch (replaceServerTtsSource clears this on
    // every dispatch, including a cache-hit replay of a DIFFERENT chunk).
    const confirmed = lastConfirmedActiveRef.current;
    if (!confirmed) return;
    // Rainer/Thorn (2026-10-10) — self-heal a premature drain. More audio
    // for the SAME owned generation can legitimately keep arriving after
    // speakingMessageId already went null (a player underrun between
    // synthesized pieces, or a manual seek into a still-incomplete cache
    // entry — neither is a real finish, since tts_end for the WHOLE
    // request hasn't arrived). Binary frames only ever route here for the
    // live server generation (the gateway serializes dispatch and a
    // superseded generation's worker is killed before it can emit more),
    // so re-adopting this id as speakingMessageId here can never resurrect
    // audio from a generation that was genuinely abandoned.
    if (speakingMessageIdRef.current !== confirmed.messageId) {
      updateSpeakingId(confirmed.messageId);
    }
    const samples = new Float32Array(data);
    audioDebug('chunk', `received ${samples.length} samples`);
    // Cache the chunk under the speaking message ID
    const cacheKey = speakingMessageIdRef.current
      ? AudioCache.makeKey(speakingMessageIdRef.current, voiceIdRef.current)
      : null;
    if (cacheKey) {
      cacheRef.current.append(cacheKey, samples, serverSampleRateRef.current);
    }
    playerRef.current.enqueue(samples, serverSampleRateRef.current);
    setVoiceState("speaking");
  }, [updateSpeakingId]);

  const onTTSStart = useCallback((messageId: string, sampleRate: number, _voiceId: string, sourceId?: string) => {
    if (ttsModeRef.current !== "server") return;
    const source = serverTtsSourceRef.current;
    // Protects an active CACHE replay from an unrelated real generation's
    // tts_start — a cache-hit replay never dispatched anything over the
    // network, so any tts_start arriving while kind is "cache" necessarily
    // belongs to some OTHER, independent lane the user isn't currently
    // listening to via this replay; let it through would hijack
    // speakingId away from the deliberate cache playback.
    if (source.kind === "cache") return;
    if (source.kind === "request" && source.requestedMessageId === messageId && source.sourceId === (sourceId ?? null)) {
      // Confirms OUR OWN outstanding manual request.
      serverTtsSourceRef.current = { ...source, activeMessageId: messageId };
      lastConfirmedActiveRef.current = { messageId, sourceId: sourceId ?? null };
    } else if (
      source.kind === "request" && source.requestedMessageId !== null
      && ttsEndedIdRef.current !== source.requestedMessageId
      && erroredMessageIdRef.current !== source.requestedMessageId
    ) {
      // Thorn (2026-10-10 review, round 2) — `source.kind` still names a
      // SPECIFIC outstanding manual request, and THAT request has not
      // reached its own terminal signal yet (neither ttsEndedId nor
      // erroredMessageId names it) — we are still genuinely, exclusively
      // waiting on it. Anything else arriving right now is presumptively
      // stale/noise (a straggling response for some EARLIER, already-
      // abandoned request, not a legitimate different lane) and must
      // still be dropped, exactly like the original code: confirming it
      // would let a stale frame hijack speakingId away from the current,
      // legitimate request. The REAL cross-lane case this file's broader
      // fix targets looks different from THIS shape: the gateway's
      // cross-mode supersede synchronously sends the manual request's OWN
      // tts_end/tts_notice (so ttsEndedId/erroredMessageId already names
      // it) BEFORE the successor auto-stream's worker can even be
      // dispatched, let alone reach ITS OWN tts_start — by the time that
      // arrives, the condition above is already false and the ELSE branch
      // below correctly confirms it.
      return;
    } else {
      // Thorn (2026-10-10 review) — everything else — an id the request
      // lane never dispatched at all — is a FRESH, independent
      // confirmation (almost always a chat message's own auto-stream) —
      // NEVER silently dropped. The old code `return`ed here whenever
      // `source.kind === "request"` named a DIFFERENT id, full stop (e.g.
      // our own manual request was just cross-lane-superseded by this
      // very auto-stream); that left lastConfirmedActiveRef stale, and
      // onServerAudioChunk's self-heal would then misattribute THIS
      // lane's own incoming audio frames to the stale, already-dead id —
      // splicing the wrong lane's audio into what looked like the
      // original reading resuming. Gateway-assigned sourceId
      // distinguishes even same-message auto generations.
      const generation = replaceServerTtsSource("auto", messageId);
      serverTtsSourceRef.current = {
        generation, kind: "auto", requestedMessageId: messageId, sourceId: sourceId ?? null, activeMessageId: messageId,
      };
      lastAutoDispatchRef.current = { requestedMessageId: messageId, sourceId: sourceId ?? null };
      lastConfirmedActiveRef.current = { messageId, sourceId: sourceId ?? null };
    }
    audioDebug('tts', `▶ tts_start msgId=${messageId.slice(0,8)} rate=${sampleRate}`);
    // Store actual sample rate so subsequent audio chunks use the correct rate
    serverSampleRateRef.current = sampleRate;
    // stop() resets sources without closing the AudioContext — always safe to call
    playerRef.current.stop();
    // Only call prime() when context doesn't already exist or isn't running.
    // On Android Chrome, ctx.resume() on a suspended context triggers an OS audio
    // session acquisition sound (the beep). If the context is already running,
    // prime() is a no-op and skipping it avoids the spurious resume() call.
    const shouldPrime = !playerRef.current.isContextRunning();
    console.log(`[HEALTH:tts-start] prime=${shouldPrime} ctxState=${playerRef.current.ctxState()} messageId=${messageId}`);
    if (shouldPrime) {
      playerRef.current.prime();
    }
    updateSpeakingId(messageId);
    setVoiceState("speaking");
  }, [updateSpeakingId, replaceServerTtsSource]);

  const onTTSEnd = useCallback((messageId: string, sourceId?: string) => {
    if (ttsModeRef.current !== "server") return;
    const source = serverTtsSourceRef.current;
    // Ownership check against the per-lane dispatch refs (see their own
    // doc comment above) — NOT serverTtsSourceRef, which a premature drain
    // may already have reset to kind:"none" by the time this real
    // terminal signal arrives.
    if (!ownsDispatchedTerminal(messageId, sourceId ?? null)) return;
    audioDebug('tts', `■ tts_end msgId=${messageId.slice(0,8)}`);
    // Mark cache complete and record the generation-terminal signal
    // UNCONDITIONALLY once ownership is confirmed — deliberately NOT gated
    // on `speakingMessageIdRef.current === messageId` the way the drain-
    // start below still is. A premature drain (underrun or a seek into an
    // incomplete cache entry) can null speakingMessageId well before this
    // tts_end — the WHOLE request's own terminal signal — actually arrives;
    // ttsEndedId is the one marker a consumer can trust regardless of
    // whether playback-level state drifted in the meantime.
    const cacheKey = AudioCache.makeKey(messageId, voiceIdRef.current);
    cacheRef.current.markComplete(cacheKey);
    markTtsEnded(messageId);
    if (speakingMessageIdRef.current !== messageId) return;
    // Start drain polling — same as browser TTS path
    startDrainPolling(messageId, source.generation);
  }, [startDrainPolling, ownsDispatchedTerminal]);

  // Reeve HIGH, PR #68 2nd round. Mirrors onTTSEnd's ownership check
  // (ownsDispatchedTerminal) so a tts_notice for a request/stream this
  // instance no longer owns — stale, superseded, or for some other
  // message entirely — can never mark the WRONG id as failed.
  // The server sends tts_end immediately before this notice on the failure
  // path (see handleManualTTSRequest/auto-stream onError in server.ts); that
  // tts_end's own drain-to-null transition is what actually releases
  // speakingId a couple hundred ms later via startDrainPolling above —
  // onTTSError only needs to record WHICH id that drain was actually a
  // failure for, not drive the transition itself. Deliberately NOT gated on
  // `speakingMessageIdRef.current === messageId` (Rainer/Thorn, 2026-10-10)
  // — same reasoning as onTTSEnd's own ttsEndedId: a worker crash arriving
  // after a premature drain (underrun/seek) must still surface as a real
  // failure, not get silently swallowed because playback-level state had
  // already drifted away from this id.
  const onTTSError = useCallback((messageId: string, sourceId?: string) => {
    if (ttsModeRef.current !== "server") return;
    // Same per-lane-dispatch-ref ownership check as onTTSEnd above —
    // survives a premature drain's reset of serverTtsSourceRef.
    if (!ownsDispatchedTerminal(messageId, sourceId ?? null)) return;
    audioDebug('tts', `✖ tts_notice (error) msgId=${messageId.slice(0,8)}`);
    markErrored(messageId);
  }, [ownsDispatchedTerminal]);

  // Override speakMessage to fall back on error state
  const speakMessageWithFallback = useCallback(
    (message: UIMessage) => {
      if (voiceState === "error") {
        if (speakingMessageIdRef.current === message.id) {
          window.speechSynthesis?.cancel();
          updateSpeakingId(null);
          return;
        }
        const text = extractTextFromMessage(message);
        if (!text) return;
        updateSpeakingId(message.id);
        speakFallback(text);
        return;
      }
      speakMessage(message);
    },
    [voiceState, speakMessage, speakFallback, updateSpeakingId],
  );

  const getAudioLevel = useCallback(() => {
    return playerRef.current.getAudioLevel();
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      workerRef.current?.terminate();
      playerRef.current.destroy();
      if (drainIntervalRef.current) clearInterval(drainIntervalRef.current);
    };
  }, []);

  return {
    voiceState,
    speakingMessageId,
    initVoice,
    speakMessage: speakMessageWithFallback,
    speakText,
    stopSpeaking,
    seekTo,
    getPlaybackProgress,
    getAudioLevel,
    audioCache: cacheRef.current,
    restartVoice,
    playbackSpeed: options.playbackSpeed ?? 1.0,
    erroredMessageId,
    ttsEndedId,
    onServerAudioChunk,
    onTTSStart,
    onTTSEnd,
    onTTSError,
  };
}
